import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Phone, User, Leaf, ExternalLink } from 'lucide-react'
import { CommunicationHistory } from './CommunicationHistory'
import type { Communication } from '../types/communication'

interface ConversationSlideOverProps {
  isOpen: boolean
  onClose: () => void
  selectedMessage: Communication | null
}

export default function ConversationSlideOver({
  isOpen,
  onClose,
  selectedMessage
}: ConversationSlideOverProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  if (!isOpen || !selectedMessage) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-over panel - bottom sheet on mobile, side panel on desktop */}
      <div
        ref={panelRef}
        className={`fixed z-50 flex flex-col bg-white dark:bg-nature-900 shadow-xl transition-transform duration-300 ease-in-out
          inset-x-0 bottom-0 h-[85vh] rounded-t-2xl
          sm:inset-y-0 sm:inset-x-auto sm:right-0 sm:h-auto sm:w-full sm:max-w-xl sm:rounded-none
          ${isOpen ? 'translate-y-0 sm:translate-x-0' : 'translate-y-full sm:translate-y-0 sm:translate-x-full'}`}
      >
        {/* Drag handle for mobile bottom sheet */}
        <div className="sm:hidden flex justify-center pt-2 pb-1 bg-nature-50 dark:bg-nature-800 rounded-t-2xl">
          <div className="w-10 h-1 bg-nature-300 dark:bg-nature-600 rounded-full" />
        </div>

        {/* Header - compact on mobile */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-800">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {/* Avatar - smaller on mobile */}
            <div className={`w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 rounded-full flex items-center justify-center ${
              selectedMessage.recipient_type === 'customer'
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
            }`}>
              {selectedMessage.recipient_type === 'customer' ? (
                <User className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <Leaf className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <h2 className="font-semibold text-sm sm:text-base text-nature-900 dark:text-nature-100 truncate">
                  {selectedMessage.recipient_name}
                </h2>
                <button
                  onClick={() => {
                    onClose()
                    navigate(
                      selectedMessage.recipient_type === 'customer'
                        ? `/admin/customers/${selectedMessage.recipient_id}`
                        : `/admin/subcontractors/${selectedMessage.recipient_id}`
                    )
                  }}
                  className="flex-shrink-0 p-1 text-nature-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  title={selectedMessage.recipient_type === 'customer' ? 'Visa kund' : 'Visa trädgårdsmästare'}
                >
                  <ExternalLink className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </button>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-nature-500">
                {/* Badge - icon only on mobile */}
                <span className={`inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full text-xs ${
                  selectedMessage.recipient_type === 'customer'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                }`}>
                  {selectedMessage.recipient_type === 'customer' ? (
                    <User className="h-3 w-3" />
                  ) : (
                    <Leaf className="h-3 w-3" />
                  )}
                  <span className="hidden sm:inline">
                    {selectedMessage.recipient_type === 'customer' ? 'Kund' : 'TG'}
                  </span>
                </span>
                {selectedMessage.recipient_phone && (
                  <a
                    href={`tel:${selectedMessage.recipient_phone}`}
                    className="flex items-center gap-1 text-primary-600 hover:underline truncate"
                  >
                    <Phone className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{selectedMessage.recipient_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1.5 sm:p-2 rounded-lg hover:bg-nature-200 dark:hover:bg-nature-700 transition-colors"
          >
            <X className="h-5 w-5 text-nature-500" />
          </button>
        </div>

        {/* Communication History */}
        <div className="flex-1 overflow-hidden">
          <CommunicationHistory
            recipientType={selectedMessage.recipient_type}
            recipientId={selectedMessage.recipient_id}
            recipientName={selectedMessage.recipient_name}
            recipientPhone={selectedMessage.recipient_phone || ''}
            hideHeader
          />
        </div>
      </div>
    </>
  )
}
